/**
 * Component-level cache refresh middleware for server-UI.
 *
 * Watches for mutations that affect server-UI components. When one
 * fulfills, fetches ONLY the affected server-UI component (not the
 * entire container) and patches it into the container cache via
 * updateQueryData. This gives instant UI updates without a full
 * container refetch.
 *
 * To add a new mutation → component mapping, add one entry to
 * MUTATION_MAP and one matcher to mutationMatchers. No new listeners needed.
 */
import { formsApi } from '@features/forms/store/api';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { enhancedApi as assistantApi } from '@store/generated/assistant';
import { enhancedApi as documentsApi } from '@store/generated/documents';
import { enhancedApi as eprescriptionsApi } from '@store/generated/eprescriptions';
import { enhancedApi as labsApi } from '@store/generated/labs';
import { enhancedApi as patientsApi } from '@store/generated/patients';
import { enhancedApi as paymentsApi } from '@store/generated/payments';

import { serverUiApi } from './api';

/**
 * Maps mutation endpoint names to the server-UI component type_key
 * that should be refreshed, plus a function to extract the patientId
 * from that mutation's originalArgs (since arg shapes differ).
 *
 * Keys are RTK Query endpoint names (auto-generated or custom).
 * If the OpenAPI generator renames an endpoint, the lookup returns
 * undefined and the mutation is ignored (no-op, not a crash).
 */
type MutationMapping = {
  typeKey: string;
  getPatientId: (originalArgs: any) => string | undefined;
  /** Delay before refetch (ms). Use when the backend processes the mutation
   *  asynchronously and the component query won't reflect the change immediately. */
  delayMs?: number;
};

const fromPatientId = (args: any): string | undefined => args?.patientId;

const MUTATION_MAP: Record<string, MutationMapping> = {
  // Clinical chart — diagnoses
  postV2ProviderPatientsByPatientIdDiagnoses: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  patchV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  deleteV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  // Clinical chart — medications
  postV2ProviderPatientsByPatientIdMedications: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  patchV2ProviderPatientsByPatientIdMedicationsAndMedicationId: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  deleteV2ProviderPatientsByPatientIdMedicationsAndMedicationId: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  // Clinical chart — allergies
  postV2ProviderPatientsByPatientIdAllergies: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  patchV2ProviderPatientsByPatientIdAllergiesAndAllergyId: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  deleteV2ProviderPatientsByPatientIdAllergiesAndAllergyId: { typeKey: 'clinical_reference', getPatientId: fromPatientId },
  // Treatment plan (v2)
  postV2ProviderPatientsByPatientIdTreatmentPlan: { typeKey: 'treatment_plan_status', getPatientId: fromPatientId },
  patchV2ProviderPatientsByPatientIdTreatmentPlan: { typeKey: 'treatment_plan_status', getPatientId: fromPatientId },
  postV2ProviderPatientsByPatientIdTreatmentPlanFromNote: { typeKey: 'treatment_plan_status', getPatientId: fromPatientId },
  postV2ProviderPatientsByPatientIdTreatmentPlanMarkReviewed: { typeKey: 'treatment_plan_status', getPatientId: fromPatientId },
  postV2ProviderPatientsByPatientIdTreatmentPlanReviewDate: { typeKey: 'treatment_plan_status', getPatientId: fromPatientId },
  postV2ProviderPatientsByPatientIdTreatmentPlanRollback: { typeKey: 'treatment_plan_status', getPatientId: fromPatientId },

  // Documents — patient_id nested in context (fragile) or in generate-and-send request
  postV2ProviderDocumentsEdit: {
    typeKey: 'document_history',
    getPatientId: (args) => args?.documentEditRequest?.context?.patient_id,
  },
  postV2ProviderDocumentsGenerateAndSend: {
    typeKey: 'document_history',
    getPatientId: (args) => args?.generateAndSendRequest?.patient_id,
  },

  // Labs — the backend generates the lab requisition document asynchronously
  // after the POST returns, so the component query won't include the new lab
  // if we refetch immediately. A 2s delay gives the backend time to finish.
  // Remove this once the backend makes lab order creation synchronous.
  postV2ProviderLabsOrders: {
    typeKey: 'lab_history',
    getPatientId: (args) => args?.createLabOrderRequest?.patient_id,
    delayMs: 2000,
  },

  // Forms (V1 endpoint, but RTK Query mutation)
  assignFormsToPatient: {
    typeKey: 'form_history',
    getPatientId: (args) => args?.patient_id,
  },

  // Payments
  postV2ProviderPaymentRequests: {
    typeKey: 'payment_history',
    getPatientId: (args) => args?.sendPaymentRequestRequest?.patient_id,
  },

  // Medication history — recording SureScripts consent flips the component
  // out of its "needed" state and (after the Celery pull) into "granted".
  // The "pulling" state has a 5-minute grace window on the backend, so a
  // short delay before the first refetch lets the resolver flip to
  // "pulling" before we paint, avoiding a brief flash of the consent CTA.
  postV2ProviderPatientsByPatientIdMedicationHistoryConsent: {
    typeKey: 'medication_history',
    getPatientId: fromPatientId,
    delayMs: 500,
  },
};

const CONTAINER_KEY = 'patient_page';

/** Build matchers for all mapped mutations. */
const mutationMatchers = [
  // Patients API — diagnoses
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdDiagnoses.matchFulfilled,
  patientsApi.endpoints.patchV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId.matchFulfilled,
  patientsApi.endpoints.deleteV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId.matchFulfilled,
  // Patients API — medications
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdMedications.matchFulfilled,
  patientsApi.endpoints.patchV2ProviderPatientsByPatientIdMedicationsAndMedicationId.matchFulfilled,
  patientsApi.endpoints.deleteV2ProviderPatientsByPatientIdMedicationsAndMedicationId.matchFulfilled,
  // Patients API — allergies
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdAllergies.matchFulfilled,
  patientsApi.endpoints.patchV2ProviderPatientsByPatientIdAllergiesAndAllergyId.matchFulfilled,
  patientsApi.endpoints.deleteV2ProviderPatientsByPatientIdAllergiesAndAllergyId.matchFulfilled,
  // Treatment plan (v2)
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdTreatmentPlan.matchFulfilled,
  patientsApi.endpoints.patchV2ProviderPatientsByPatientIdTreatmentPlan.matchFulfilled,
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdTreatmentPlanFromNote.matchFulfilled,
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdTreatmentPlanMarkReviewed.matchFulfilled,
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdTreatmentPlanReviewDate.matchFulfilled,
  patientsApi.endpoints.postV2ProviderPatientsByPatientIdTreatmentPlanRollback.matchFulfilled,
  // Documents
  assistantApi.endpoints.postV2ProviderDocumentsEdit.matchFulfilled,
  documentsApi.endpoints.postV2ProviderDocumentsGenerateAndSend.matchFulfilled,
  // Labs
  labsApi.endpoints.postV2ProviderLabsOrders.matchFulfilled,
  // Forms
  formsApi.endpoints.assignFormsToPatient.matchFulfilled,
  // Payments
  paymentsApi.endpoints.postV2ProviderPaymentRequests.matchFulfilled,
  // Medication history — consent
  eprescriptionsApi.endpoints.postV2ProviderPatientsByPatientIdMedicationHistoryConsent.matchFulfilled,
] as const;

export const componentRefreshMiddleware = createListenerMiddleware();

componentRefreshMiddleware.startListening({
  matcher: isAnyOf(...mutationMatchers),
  effect: async (action, listenerApi) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const endpointName = (action as any).meta?.arg?.endpointName as string | undefined;
    if (!endpointName) return;

    const mapping = MUTATION_MAP[endpointName];
    if (!mapping) return;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const originalArgs = (action as any).meta?.arg?.originalArgs;
    const patientId = mapping.getPatientId(originalArgs);
    if (!patientId) return;

    // Some mutations are processed asynchronously by the backend — wait before refetching
    if (mapping.delayMs) {
      await listenerApi.delay(mapping.delayMs);
    }

    // Fetch only the affected component
    const result = await listenerApi.dispatch(
      serverUiApi.endpoints.getV2ProviderContainersByContainerKeyComponentsAndTypeKey.initiate(
        { containerKey: CONTAINER_KEY, typeKey: mapping.typeKey, entityId: patientId },
        { forceRefetch: true },
      ),
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const freshComponent = (result as any)?.data;
    if (!freshComponent) {
      // Component fetch failed — fall back to full container refetch
      listenerApi.dispatch(
        serverUiApi.util.invalidateTags([
          { type: 'ServerUi' as const, id: `${CONTAINER_KEY}:${patientId}` },
        ]),
      );
      return;
    }

    // Patch the component into the container cache.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
    (listenerApi.dispatch as any)(
      serverUiApi.util.updateQueryData(
        'getV2ProviderContainersByContainerKey',
        { containerKey: CONTAINER_KEY, entityId: patientId },
        (draft) => {
          // Search sections
          for (const section of draft.sections) {
            const idx = section.components.findIndex(c => c.type_key === mapping.typeKey);
            if (idx !== -1) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              section.components[idx].data = freshComponent.data;
              return;
            }
          }
          // Search top_level
          const topIdx = draft.top_level.findIndex(c => c.type_key === mapping.typeKey);
          if (topIdx !== -1) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            draft.top_level[topIdx].data = freshComponent.data;
          }
        },
      ),
    );
  },
});
